/**
 * Migration from the LEGACY Bible Strong app (React Native, Play Store 1.0.0).
 *
 * Single entry point: `planMigrationFromFile(bytes)` converts legacy file BYTES
 * to a row plan, or an explicit rejection.
 * Ver `docs/LEGACY_MIGRATION.md`.
 */

import { decodeText, detectLegacySource } from "./detect";
import { parseLegacyHighlightDb, parseLegacyPreferences } from "./parse";
import { buildImportPlan } from "./plan";
import type { LegacyImportPlan, LegacyRejectCode } from "./types";

export * from "./types";
export { decodeText, type LegacyDetection, detectLegacySource } from "./detect";
export {
  coordKey,
  LEGACY_VERSION,
  parseLegacyHighlightDb,
  parseLegacyPreferences,
  refLabel,
} from "./parse";
export {
  buildImportPlan,
  LEGACY_IMPORT_GROUP_COLOR,
  LEGACY_IMPORT_NAMESPACE,
  LEGACY_IMPORT_ORIGIN,
  resolveImport,
} from "./plan";
export { buildImportReport, importReportToJson, reportProblemCount } from "./report";
export {
  looksLikeSqlite,
  readSqliteRows,
  readSqliteTable,
  SqliteReadError,
  type SqliteValue,
} from "./sqlite";

export type PlanFromFileResult =
  | { ok: true; plan: LegacyImportPlan }
  | { ok: false; code: LegacyRejectCode; detail: string };

/**
 * Detects, reads, and plans in a single step from file bytes.
 *
 * Accepts either legacy source: the SQLite `highlight` (carrying
 * highlights) or the `persist-rootTest5` dump (carrying preferences).
 */
export function planMigrationFromFile(bytes: Uint8Array): PlanFromFileResult {
  const detection = detectLegacySource(bytes);
  if (!detection.ok) {
    return { ok: false, code: detection.code, detail: detection.detail };
  }

  if (detection.source === "highlight-sqlite-v1") {
    const parsed = parseLegacyHighlightDb(bytes);
    if (!parsed.ok) {
      return { ok: false, code: parsed.code, detail: parsed.detail };
    }
    return {
      ok: true,
      plan: buildImportPlan(["highlight-sqlite-v1"], parsed.highlights, parsed.issues),
    };
  }

  const parsed = parseLegacyPreferences(decodeText(bytes));
  if (!parsed.ok) {
    return { ok: false, code: parsed.code, detail: parsed.detail };
  }
  return { ok: true, plan: buildImportPlan(["redux-persist-v1"], [], [], parsed.preferences) };
}

/**
 * Plans from the TWO sources read on-device by native command
 * (`migrate_legacy_v1`): el SQLite de resaltados y el JSON de preferencias.
 * Either source may be missing.
 */
export function planMigrationFromDevice(input: {
  highlightDb?: Uint8Array;
  preferencesJson?: string;
}): PlanFromFileResult {
  const sources: LegacyImportPlan["sources"] = [];
  let highlights: Parameters<typeof buildImportPlan>[1] = [];
  let issues: Parameters<typeof buildImportPlan>[2] = [];
  let preferences: Parameters<typeof buildImportPlan>[3];

  if (input.highlightDb && input.highlightDb.length > 0) {
    const parsed = parseLegacyHighlightDb(input.highlightDb);
    if (!parsed.ok) {
      return { ok: false, code: parsed.code, detail: parsed.detail };
    }
    sources.push("highlight-sqlite-v1");
    ({ highlights } = parsed);
    ({ issues } = parsed);
  }

  if (input.preferencesJson && input.preferencesJson.trim().length > 0) {
    const parsed = parseLegacyPreferences(input.preferencesJson);
    // Unreadable preferences must NOT fail highlight migration,
    // which is the data that truly matters.
    if (parsed.ok) {
      sources.push("redux-persist-v1");
      ({ preferences } = parsed);
    }
  }

  if (sources.length === 0) {
    return {
      ok: false,
      code: "unrecognised-file",
      detail: "No legacy data found on this device.",
    };
  }
  return { ok: true, plan: buildImportPlan(sources, highlights, issues, preferences) };
}
